import { yupResolver } from '@hookform/resolvers/yup';
import * as Ramda from 'ramda';
import * as Yup from 'yup';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { Button, DialogContent } from '@mui/material';

import { CardContainer } from './shared/StyledCardContainer';
import { AddItemDialogActions } from './shared/AddItemDialogActions';
import { ControlledTextField } from './shared/common';

const NAME_MIN_LENGTH = 2;
const NAME_MAX_LENGTH = 512;

export const PropertyManagerNamesModal = ({
  data,
  close,
}: PropertyManagerNamesModalProps) => {
  const formMethods = useForm<PropertyManagerNamesFormValues>({
    defaultValues: { names: data.names },
    resolver: yupResolver(schema),
  });

  const names = useFieldArray({ control: formMethods.control, name: 'names' });

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit(
          Ramda.compose(close, (values: PropertyManagerNamesFormValues) =>
            data.onSubmit(values.names)
          )
        )}
      >
        <DialogContent style={{ padding: '1rem 2.5rem' }}>
          {names.fields.map((field, index) => (
            <CardContainer key={field.id} elevation={3}>
              <ControlledTextField
                label="Property Manager Name"
                required
                name={`names.${index}.value`}
              />
              <Button
                className="contrast-text"
                style={{ position: 'absolute', top: 5, right: 5 }}
                onClick={() => names.remove(index)}
              >
                Remove
              </Button>
            </CardContainer>
          ))}
        </DialogContent>
        <AddItemDialogActions
          addLabel="Add Property Manager"
          onAdd={() => names.append({ value: '' })}
          onCancel={close}
        />
      </form>
    </FormProvider>
  );
};

export const PropertyManagerNamesModalConfig = {
  PropertyManagerNamesModal: {
    component: PropertyManagerNamesModal,
    config: {
      title: 'Scheduled Property Managers',
      fullWidth: true,
      maxWidth: 'sm',
    },
  },
};

export type PropertyManagerName = { value: string };

type PropertyManagerNamesFormValues = { names: PropertyManagerName[] };

type PropertyManagerNamesModalProps = Readonly<{
  data: {
    names: PropertyManagerName[];
    onSubmit: (names: PropertyManagerName[]) => void;
  };
  close: () => void;
}>;

const schema = Yup.object().shape({
  names: Yup.array().of(
    Yup.object().shape({
      value: Yup.string()
        .trim()
        .min(NAME_MIN_LENGTH)
        .max(NAME_MAX_LENGTH)
        .label('Property Manager name')
        .required(),
    })
  ),
});
